import React, { useEffect, useState } from 'react';
import {
  Card, Button, Input, Table, Statistic, Row, Col, Typography, Alert,
  Popconfirm, Segmented, Collapse, theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ThunderboltOutlined, CalculatorOutlined, SaveOutlined, DeleteOutlined,
  HistoryOutlined, BookOutlined, SlidersOutlined,
} from '@ant-design/icons';
import { CalculationMode, PowerResult } from '../../models/types';
import { savePowerResult, getPowerResults, deletePowerResult } from '../../firebase/firestore';
import {
  calcThreePhaseAmperage, calcThreePhaseVoltage, calcThreePhasePower,
  calcSinglePhaseInductiveAmperage, calcSinglePhaseInductiveVoltage, calcSinglePhaseInductivePower,
  calcSinglePhaseResistiveAmperage, calcSinglePhaseResistiveVoltage, calcSinglePhaseResistivePower,
} from './powerCalculations';

type LoadType = 'three-phase-inductive' | 'single-phase-inductive' | 'single-phase-resistive';

interface Props { projectId: string; }

const LOAD_LABELS: Record<LoadType, string> = {
  'three-phase-inductive': '3-Phase Inductive',
  'single-phase-inductive': '1-Phase Inductive',
  'single-phase-resistive': '1-Phase Resistive',
};

const LOAD_FORMULAS: Record<LoadType, string> = {
  'three-phase-inductive': 'kW = A × V × 1.732 × PF × Eff ÷ 1000',
  'single-phase-inductive': 'kW = A × V × PF × Eff ÷ 1000',
  'single-phase-resistive': 'kW = A × V ÷ 1000',
};

const PowerModule: React.FC<Props> = ({ projectId }) => {
  const { token } = theme.useToken();
  const [loadType, setLoadType] = useState<LoadType>('three-phase-inductive');
  const [mode, setMode] = useState<CalculationMode>('amperage');
  const [kw, setKw] = useState('10');
  const [voltage, setVoltage] = useState('480');
  const [amperage, setAmperage] = useState('');
  const [pf, setPf] = useState('0.85');
  const [eff, setEff] = useState('95');
  const [notes, setNotes] = useState('');
  const [result, setResult] = useState<number | null>(null);
  const [resultLabel, setResultLabel] = useState('');
  const [resultUnit, setResultUnit] = useState('');
  const [saved, setSaved] = useState<PowerResult[]>([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    getPowerResults(projectId)
      .then(setSaved).catch(() => setError('Failed to load')).finally(() => setLoading(false));
  }, [projectId]);

  const needsPf = loadType !== 'single-phase-resistive';
  const n = (v: string) => parseFloat(v) || 0;

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    const kwN = n(kw), vN = n(voltage), aN = n(amperage), pfN = n(pf), effN = n(eff);

    if (mode !== 'power') {
      if (!kw || kwN <= 0) errors.kw = 'Must be a positive number';
    }
    if (mode !== 'voltage') {
      if (!voltage || vN <= 0) errors.voltage = 'Must be a positive number';
    }
    if (mode !== 'amperage') {
      if (!amperage || aN <= 0) errors.amperage = 'Must be a positive number';
    }
    if (needsPf) {
      if (pfN <= 0 || pfN > 1) errors.pf = 'Must be between 0 and 1';
      if (effN <= 0 || effN > 100) errors.eff = 'Must be between 0 and 100';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculate = () => {
    if (!validate()) return;
    const kwN = n(kw), vN = n(voltage), aN = n(amperage), pfN = n(pf), effN = n(eff);
    let val: number | null = null;
    let label = '', unit = '';

    if (loadType === 'three-phase-inductive') {
      if (mode === 'amperage' && kwN && vN) { val = calcThreePhaseAmperage(kwN, vN, pfN, effN); label = 'Amperage'; unit = 'A'; }
      else if (mode === 'voltage' && kwN && aN) { val = calcThreePhaseVoltage(kwN, aN, pfN, effN); label = 'Voltage'; unit = 'V'; }
      else if (mode === 'power' && aN && vN) { val = calcThreePhasePower(aN, vN, pfN, effN); label = 'Power'; unit = 'kW'; }
    } else if (loadType === 'single-phase-inductive') {
      if (mode === 'amperage' && kwN && vN) { val = calcSinglePhaseInductiveAmperage(kwN, vN, pfN, effN); label = 'Amperage'; unit = 'A'; }
      else if (mode === 'voltage' && kwN && aN) { val = calcSinglePhaseInductiveVoltage(kwN, aN, pfN, effN); label = 'Voltage'; unit = 'V'; }
      else if (mode === 'power' && aN && vN) { val = calcSinglePhaseInductivePower(aN, vN, pfN, effN); label = 'Power'; unit = 'kW'; }
    } else {
      if (mode === 'amperage' && kwN && vN) { val = calcSinglePhaseResistiveAmperage(kwN, vN); label = 'Amperage'; unit = 'A'; }
      else if (mode === 'voltage' && kwN && aN) { val = calcSinglePhaseResistiveVoltage(kwN, aN); label = 'Voltage'; unit = 'V'; }
      else if (mode === 'power' && aN && vN) { val = calcSinglePhaseResistivePower(aN, vN); label = 'Power'; unit = 'kW'; }
    }
    setResult(val); setResultLabel(label); setResultUnit(unit);
  };

  const handleSave = async () => {
    if (result == null) return;
    setSaving(true); setError('');
    try {
      const entry: Omit<PowerResult, 'id'> = {
        projectId, loadType, mode,
        kw: n(kw), voltage: n(voltage), amperage: n(amperage),
        powerFactor: needsPf ? n(pf) : undefined,
        efficiency: needsPf ? n(eff) : undefined,
        resultAmperage: resultLabel === 'Amperage' ? result : undefined,
        resultVoltage: resultLabel === 'Voltage' ? result : undefined,
        resultKw: resultLabel === 'Power' ? result : undefined,
        notes, createdAt: new Date().toISOString(),
      };
      const id = await savePowerResult(entry);
      setSaved(prev => [{ ...entry, id }, ...prev]);
      setResult(null); setNotes('');
    } catch (err: any) {
      console.error('Power save error:', err);
      setError(`Failed to save: ${err?.message ?? err?.code ?? 'Unknown error'}`);
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deletePowerResult(projectId, id); setSaved(prev => prev.filter(r => r.id !== id)); }
    catch { setError('Failed to delete'); }
  };

  const REFERENCE = [
    { type: 'Resistive Loads', description: 'Loads which consume only active power. The voltage and current waveforms are in phase with each other.', pf: 'Power factor is Unity (1.0). Power always flows from source to load.', examples: 'Incandescent lights, heaters, and any load consisting only of heating elements.' },
    { type: 'Inductive Loads', description: 'Loads which consume active and reactive power. Voltage and current waveforms are out of phase.', pf: 'PF varies between 0.0 and 1.0. Power flows from source to load or load to source.', examples: 'Fluorescent lights, transformers, and motors.' },
    { type: 'Capacitive Loads', description: 'Introduced when inductive loads are energized. Correct reactive power consumption and drive PF toward Unity.', pf: 'PF leads the voltage (rather than lagging). Used to offset lagging PF of inductive loads.', examples: 'Capacitors installed parallel to motor power input leads.' },
    { type: 'Lighting Loads', description: 'LED systems driven by electronic drivers with inherent capacitive characteristics and integrated active PF correction.', pf: 'Approximately 0.95 for fluorescent lights; LED drivers achieve ~0.99 PF.', examples: 'LED lighting, fluorescent lights with electronic ballasts.' },
    { type: 'Motor Loads', description: 'PF measures how effectively a motor circuit uses supplied power. High PF = most power does useful work. Low PF = significant waste due to reactive components.', pf: 'VFDs improve PF, typically approaching Unity. Recommended for oversized motors or low-PF operation.', examples: 'Electric motors, especially AC induction motors.' },
  ];

  const numField = (label: string, key: string, value: string, onChange: (v: string) => void, extra?: { min?: number; max?: number; step?: number }) => (
    <div>
      <div style={{ fontSize: 13, marginBottom: 6 }}>{label}</div>
      <Input
        type="number" value={value}
        min={extra?.min} max={extra?.max} step={extra?.step}
        status={fieldErrors[key] ? 'error' : undefined}
        onChange={e => { onChange(e.target.value); setFieldErrors(fe => ({ ...fe, [key]: '' })); }}
      />
      {fieldErrors[key] && <Typography.Text type="danger" style={{ fontSize: 12 }}>{fieldErrors[key]}</Typography.Text>}
    </div>
  );

  const savedColumns: ColumnsType<PowerResult> = [
    { title: 'Load Type', dataIndex: 'loadType', render: (v: LoadType) => LOAD_LABELS[v] },
    { title: 'Solve For', dataIndex: 'mode' },
    {
      title: 'Result', key: 'result',
      render: (_, r) => (
        <Typography.Text style={{ color: token.colorSuccess }}>
          {r.resultAmperage != null ? `${r.resultAmperage.toFixed(2)} A`
            : r.resultVoltage != null ? `${r.resultVoltage.toFixed(2)} V`
            : r.resultKw != null ? `${r.resultKw.toFixed(3)} kW` : '—'}
        </Typography.Text>
      ),
    },
    { title: 'Notes', dataIndex: 'notes', render: v => v ? v : <Typography.Text type="secondary">—</Typography.Text> },
    { title: 'Date', dataIndex: 'createdAt', width: 120, render: v => new Date(v).toLocaleDateString() },
    {
      title: '', key: 'actions', width: 50, align: 'right',
      render: (_, r) => (
        <Popconfirm title="Delete this result?" onConfirm={() => handleDelete(r.id!)} okText="Delete" okButtonProps={{ danger: true }}>
          <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Delete result" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {/* Page head */}
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '16px 24px' } }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <div>
            <Typography.Title level={4} style={{ margin: 0 }}>
              <ThunderboltOutlined style={{ color: token.colorPrimary, marginRight: 8 }} />Power
            </Typography.Title>
            <Typography.Text type="secondary">Electrical load calculator</Typography.Text>
          </div>
        </div>
      </Card>

      {error && <Alert type="error" showIcon message={error} closable onClose={() => setError('')} style={{ marginBottom: 16 }} />}

      <Row gutter={[16, 16]}>
        {/* LEFT: inputs */}
        <Col xs={24} lg={10}>
          <Card title={<span><SlidersOutlined /> Inputs</span>}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, marginBottom: 6 }}>Load Type</div>
              <Segmented
                block
                value={loadType}
                options={(Object.keys(LOAD_LABELS) as LoadType[]).map(lt => ({ value: lt, label: LOAD_LABELS[lt] }))}
                onChange={v => { setLoadType(v as LoadType); setResult(null); setAmperage(''); setKw('10'); setVoltage('480'); setFieldErrors({}); }}
              />
              <Typography.Text code style={{ fontSize: 12, display: 'inline-block', marginTop: 8 }}>
                {LOAD_FORMULAS[loadType]}
              </Typography.Text>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 13, marginBottom: 6 }}>Solve For</div>
              <Segmented
                block
                value={mode}
                options={(['amperage', 'voltage', 'power'] as CalculationMode[]).map(m => ({
                  value: m,
                  label: m === 'amperage' ? 'Amperage (A)' : m === 'voltage' ? 'Voltage (V)' : 'Power (kW)',
                }))}
                onChange={v => { setMode(v as CalculationMode); setResult(null); setFieldErrors({}); }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              {mode !== 'power' && numField('Power (kW)', 'kw', kw, setKw)}
              {mode !== 'voltage' && numField('Voltage (V)', 'voltage', voltage, setVoltage)}
              {mode !== 'amperage' && numField('Amperage (A)', 'amperage', amperage, setAmperage)}
              {needsPf && (
                <>
                  {numField('Power Factor (0–1)', 'pf', pf, setPf, { min: 0, max: 1, step: 0.01 })}
                  {numField('Efficiency (%)', 'eff', eff, setEff, { min: 0, max: 100 })}
                </>
              )}
            </div>

            <Button type="primary" block icon={<CalculatorOutlined />} onClick={calculate}>Calculate</Button>
          </Card>
        </Col>

        {/* RIGHT: result */}
        <Col xs={24} lg={14}>
          {result !== null ? (
            <>
              <Card style={{ marginBottom: 16 }} styles={{ body: { padding: '20px 24px' } }}>
                <Statistic
                  title={resultLabel}
                  value={result.toFixed(result < 100 ? 2 : 1)}
                  suffix={<Typography.Text type="secondary" style={{ fontSize: 15 }}>{resultUnit}</Typography.Text>}
                  valueStyle={{ color: token.colorPrimary }}
                />
                <Typography.Text type="secondary" style={{ fontSize: 13 }}>
                  {LOAD_LABELS[loadType]} · {LOAD_FORMULAS[loadType]}
                </Typography.Text>
              </Card>
              <Card styles={{ body: { display: 'flex', gap: 10, alignItems: 'center' } }}>
                <Input style={{ flex: 1 }} placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} />
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>Save</Button>
              </Card>
            </>
          ) : (
            <Alert type="info" showIcon message="Enter your values and press Calculate to see the result." />
          )}
        </Col>
      </Row>

      {/* Reference content */}
      <Card title={<span><ThunderboltOutlined /> Power Factor (PF) — Definition &amp; Effect</span>} style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, color: token.colorTextSecondary }}>
          <p>Power factor is the ratio between the actual electrical power dissipated by inductive AC circuits and the product of the RMS values of current and voltage.</p>
          <p>Work performed by inductive circuits is accomplished by <strong>Active Power</strong>. Additional power that does not perform work is <strong>Reactive Power</strong> — caused by reactance in AC inductive circuits.</p>
          <p>PF values range from 0.0 to 1.0, with 1.0 being Unity. Overall power utilization improves as PF approaches 1.</p>
          <p style={{ marginBottom: 6 }}>PF can be improved by:</p>
          <ul style={{ marginBottom: 8, paddingLeft: 18 }}>
            <li><strong>Variable Frequency Drives (VFDs)</strong> — automatically correct PF, typically to 0.90–0.95. Recommended for motors &gt;5 HP.</li>
            <li><strong>Power Correction Capacitors (PCCs)</strong> — install parallel to motor input leads. Do not correct above 0.95 (leading PF is undesirable).</li>
          </ul>
          <p style={{ margin: 0 }}>Caution: correcting PF to Unity (1.0) may cause input amperage to lead voltage, which is not desirable.</p>
        </div>
      </Card>

      <Card
        title={<span><BookOutlined /> Efficient Operation of Electrical Loads</span>}
        style={{ marginTop: 16 }}
        styles={{ body: { padding: 0 } }}
      >
        <Collapse
          ghost
          items={REFERENCE.map((item, idx) => ({
            key: String(idx),
            label: item.type,
            children: (
              <>
                <Typography.Paragraph>{item.description}</Typography.Paragraph>
                <Typography.Paragraph><strong>Power Factor:</strong> {item.pf}</Typography.Paragraph>
                <Typography.Paragraph style={{ marginBottom: 0 }}><strong>Examples:</strong> {item.examples}</Typography.Paragraph>
              </>
            ),
          }))}
        />
      </Card>

      {/* Saved Results */}
      <Card
        title={<span><HistoryOutlined /> Saved Results</span>}
        style={{ marginTop: 16 }}
        styles={{ body: { padding: 0 } }}
      >
        <Table<PowerResult>
          rowKey="id"
          size="middle"
          loading={loading}
          columns={savedColumns}
          dataSource={saved}
          pagination={saved.length > 20 ? { pageSize: 20 } : false}
          locale={{ emptyText: 'No results saved yet.' }}
        />
      </Card>
    </div>
  );
};

export default PowerModule;
